// src/pages/Documents/Categories.jsx
import React, { useEffect, useState } from 'react';
import { Table, Button, Container, Alert, Pagination, Modal, Form, Spinner } from 'react-bootstrap';
import { FaEdit, FaTrash, FaPlus } from 'react-icons/fa';
import { documentAPI } from '../../api/wbuoaApi';

const DocumentCategories = () => {
  // State management
  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [currentCategory, setCurrentCategory] = useState({
    id: null,
    category_name: '',
    description: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const itemsPerPage = 10;

  // Form label style
  const formLabelStyle = {
    color: '#1a4a2f',
    fontWeight: '500'
  };

  // Fetch categories on component mount
  useEffect(() => {
    fetchCategories();
  }, []);

  // Apply filters when categories or search term changes
  useEffect(() => {
    applyFilters();
  }, [categories, searchTerm, currentPage]);

  const fetchCategories = async () => {
    setIsLoading(true);
    try {
      const response = await documentAPI.listCategories();
      // API returns array directly, not wrapped in data property
      const categoriesData = Array.isArray(response) ? response : (response.data || []);
      setCategories(categoriesData);
      setTotalItems(categoriesData.length);
      setError(null);
    } catch (err) {
      setError('Failed to fetch categories: ' + (err.message || 'Server error'));
    } finally {
      setIsLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...categories];

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(cat =>
        cat.category_name?.toLowerCase().includes(term) ||
        cat.description?.toLowerCase().includes(term)
      );
    }

    // Calculate pagination
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginated = filtered.slice(startIndex, endIndex);

    setFilteredCategories(paginated);
    setTotalItems(filtered.length);
    setTotalPages(Math.ceil(filtered.length / itemsPerPage));
  };

  // Validate form function
  const validateForm = () => {
    const errors = {};
    if (!currentCategory.category_name.trim()) {
      errors.category_name = 'Category name is required';
    }
    return errors;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setFormErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      if (currentCategory.id) {
        // Update existing category
        await documentAPI.updateCategory(currentCategory.id, currentCategory);
        setSuccess('Category updated successfully');
      } else {
        // Create new category
        await documentAPI.createCategory(currentCategory);
        setSuccess('Category created successfully');
      }

      // Refresh the list
      await fetchCategories();
      setShowModal(false);
      setCurrentCategory({ id: null, category_name: '', description: '' });

      // Auto-clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error submitting category:', err);
      setError(`Operation failed: ${err.response?.data?.message || err.message || 'Please try again'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle category deletion
  const handleDelete = async (id) => {
    if (!id) {
      setError('Invalid category ID');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this category? This action cannot be undone.')) {
      return;
    }

    setDeletingId(id);
    setError(null);
    setSuccess(null);

    try {
      await documentAPI.deleteCategory(id);
      setSuccess('Category deleted successfully');

      // Refresh the list
      await fetchCategories();

      // Reset to first page if needed
      if (filteredCategories.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      }

      // Auto-clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error deleting category:', err);
      setError(`Failed to delete category: ${err.message || 'Server error'}`);
    } finally {
      setDeletingId(null);
    }
  };

  // Handle edit button click
  const handleEdit = (category) => {
    if (!category?.id) {
      setError('Invalid category data');
      return;
    }

    setCurrentCategory({
      id: category.id,
      category_name: category.category_name || '',
      description: category.description || ''
    });
    setFormErrors({});
    setShowModal(true);
  };

  // Handle create new category
  const handleCreate = () => {
    setCurrentCategory({
      id: null,
      category_name: '',
      description: ''
    });
    setFormErrors({});
    setShowModal(true);
  };

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setCurrentCategory(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when field is edited
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Generate pagination items
  const renderPagination = () => {
    if (totalPages <= 1) return null;

    let items = [];
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    items.push(
      <Pagination.Prev
        key="prev"
        onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
        disabled={currentPage === 1}
      />
    );

    if (startPage > 1) {
      items.push(
        <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
          1
        </Pagination.Item>
      );
      if (startPage > 2) {
        items.push(<Pagination.Ellipsis key="start-ellipsis" />);
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <Pagination.Item
          key={i}
          active={i === currentPage}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </Pagination.Item>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        items.push(<Pagination.Ellipsis key="end-ellipsis" />);
      }
      items.push(
        <Pagination.Item
          key={totalPages}
          onClick={() => handlePageChange(totalPages)}
        >
          {totalPages}
        </Pagination.Item>
      );
    }

    items.push(
      <Pagination.Next
        key="next"
        onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
        disabled={currentPage === totalPages}
      />
    );

    return items;
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return new Date(dateString).toLocaleDateString(undefined, options);
    } catch {
      return dateString;
    }
  };

  return (
    <Container className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1">Document Categories</h2>
          <p className="text-muted mb-0">Manage document categories for better organization</p>
        </div>
        <Button variant="primary" onClick={handleCreate} disabled={isLoading}>
          <FaPlus className="me-2" />
          Create Category
        </Button>
      </div>

      {/* Search Bar */}
      <div className="mb-4">
        <div className="input-group">
          <input
            type="text"
            className="form-control"
            placeholder="Search categories by name or description..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1); // Reset to first page when searching
            }}
          />
          {searchTerm && (
            <Button
              variant="outline-secondary"
              onClick={() => setSearchTerm('')}
            >
              Clear
            </Button>
          )}
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible className="mb-4">
          <Alert.Heading>Error</Alert.Heading>
          <p>{error}</p>
        </Alert>
      )}
      {success && (
        <Alert variant="success" onClose={() => setSuccess(null)} dismissible className="mb-4">
          <Alert.Heading>Success</Alert.Heading>
          <p>{success}</p>
        </Alert>
      )}

      {/* Stats Card */}
      <div className="card mb-4 border-0 shadow-sm">
        <div className="card-body py-3">
          <div className="row">
            <div className="col-md-3">
              <div className="d-flex justify-content-between">
                <div>
                  <h6 className="text-muted mb-0">Total Categories</h6>
                  <h4 className="mb-0">{totalItems}</h4>
                </div>
                <div className="text-primary">
                  <FaPlus size={24} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Categories Table */}
      <div className="card border-0 shadow-sm">
        <div className="card-body p-0">
          {isLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3">Loading categories...</p>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <Table hover className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Category Name</th>
                      <th>Description</th>
                      <th>Created At</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCategories.length > 0 ? (
                      filteredCategories.map(category => (
                        <tr key={category.id}>
                          <td>
                            <div className="fw-semibold">{category.category_name}</div>
                          </td>
                          <td>
                            <div className="text-truncate" style={{ maxWidth: '250px' }} title={category.description}>
                              {category.description || 'No description'}
                            </div>
                          </td>
                          <td>{formatDate(category.created_at)}</td>
                          <td>
                            <div className="d-flex gap-2">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => handleEdit(category)}
                                disabled={deletingId !== null}
                                title="Edit"
                              >
                                <FaEdit />
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleDelete(category.id)}
                                disabled={deletingId !== null}
                                title="Delete"
                              >
                                {deletingId === category.id ? (
                                  <Spinner animation="border" size="sm" />
                                ) : (
                                  <FaTrash />
                                )}
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4" className="text-center py-5">
                          <div className="py-4">
                            <FaPlus size={48} className="text-muted mb-3" />
                            <h5>No categories found</h5>
                            <p className="text-muted">
                              {searchTerm
                                ? 'No categories match your search. Try different keywords.'
                                : 'Create your first category to get started'}
                            </p>
                            {searchTerm ? (
                              <Button variant="outline-primary" onClick={() => setSearchTerm('')}>
                                Clear Search
                              </Button>
                            ) : (
                              <Button variant="primary" onClick={handleCreate}>
                                <FaPlus className="me-2" /> Create Category
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>

              {/* Pagination */}
              {filteredCategories.length > 0 && totalPages > 1 && (
                <div className="p-3 border-top">
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="text-muted">
                      Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} categories
                    </div>
                    <Pagination className="mb-0">
                      {renderPagination()}
                    </Pagination>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Category Modal */}
      <Modal show={showModal} onHide={() => !isSubmitting && setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{currentCategory.id ? 'Edit Category' : 'Create New Category'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label style={formLabelStyle}>Category Name *</Form.Label>
              <Form.Control
                type="text"
                name="category_name"
                value={currentCategory.category_name}
                onChange={handleChange}
                isInvalid={!!formErrors.category_name}
                placeholder="Enter category name"
                required
                disabled={isSubmitting}
              />
              <Form.Control.Feedback type="invalid">
                {formErrors.category_name}
              </Form.Control.Feedback>
              <Form.Text className="text-muted">
                This name will be used to categorize documents
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label style={formLabelStyle}>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={currentCategory.description}
                onChange={handleChange}
                placeholder="Enter category description (optional)"
                disabled={isSubmitting}
              />
              <Form.Text className="text-muted">
                Optional description to provide more context about this category
              </Form.Text>
            </Form.Group>

            <div className="d-flex justify-content-end gap-2 mt-4">
              <Button
                variant="secondary"
                onClick={() => setShowModal(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    {currentCategory.id ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  currentCategory.id ? 'Update Category' : 'Create Category'
                )}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default DocumentCategories;